import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { ManagementFormLayout } from 'components/ManagementFormLayout'
import { routes } from 'constants/routes'
import { adCodeStrings, commonStrings } from 'constants/strings'
import { useAdCode, useAdCodeMutations } from 'hooks'
import { useOrgPath } from 'hooks/useOrgPath'
import { toErrorMessage } from 'hooks/utils'
import type { TUpsertAdCodeRequest } from 'types'
import { AdCodeForm } from '../Form/AdCodeForm'

export default function AdCodeEdit() {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const { adCodeId } = useParams<{ adCodeId: string }>()
  const numericId = Number(adCodeId)
  // フォームに指標は出さないので、全期間の重い集計を省いて取得する
  const { adCode, loading, error: loadError } = useAdCode(numericId, {
    metrics: false,
  })
  const { update, saving } = useAdCodeMutations()
  const [error, setError] = useState<string | undefined>()
  const submitting = useRef(false)

  const detailPath = orgPathFor(
    routes.AD_CODES_DETAIL.replace(':adCodeId', String(numericId)),
  )

  const handleSubmit = (request: TUpsertAdCodeRequest) => {
    if (submitting.current) return
    submitting.current = true
    setError(undefined)
    update.mutate(
      { adCodeId: numericId, request },
      {
        onSuccess: () =>
          navigate(detailPath, {
            replace: true,
            state: { snackbarMessage: adCodeStrings.messages.updated },
          }),
        onError: (mutationError) =>
          setError(
            toErrorMessage(mutationError, adCodeStrings.errors.saveFailed),
          ),
        onSettled: () => {
          submitting.current = false
        },
      },
    )
  }

  return (
    <ManagementFormLayout
      kicker={adCodeStrings.breadcrumb}
      title={adCodeStrings.editTitle}
      description={adCodeStrings.description}
      backTo={orgPathFor(routes.AD_CODES)}
    >
      {loadError && <Alert>{loadError}</Alert>}
      {loading && !adCode ? (
        <p className="text-sm text-muted">{commonStrings.loading}</p>
      ) : adCode ? (
        <AdCodeForm
          initial={adCode}
          saving={saving}
          error={error}
          submitLabel={adCodeStrings.form.submitUpdate}
          onSubmit={handleSubmit}
          onCancel={() => navigate(detailPath)}
        />
      ) : null}
    </ManagementFormLayout>
  )
}
